import { createPortal } from 'react-dom'
import { proxy, useSnapshot } from 'valtio'
import { useEffect, useRef, useState } from 'react'
import { activeModalStack, miscUiState, openOptionsMenu } from '../globalState'
import { withInjectableUi } from './extendableSystem'
import { privateConnection } from '../threecube/singleplayer'
import { createGuestProfile, getGuestProfile, guestConnection, type GuestProfile } from '../threecube/guest'
import '../threecube/launcher.css'
export const mainMenuState = proxy({ serviceWorkerLoaded: false })

function MainMenuRenderAppBase() {
  const modals = useSnapshot(activeModalStack).length
  const { gameLoaded, fsReady } = useSnapshot(miscUiState)
  const [authorized, setAuthorized] = useState(false)
  const [authMode, setAuthMode] = useState<'guest' | 'microsoft' | null>(null)
  const [guest, setGuest] = useState<GuestProfile | null>(null)
  const [displayName, setDisplayName] = useState('')
  const [busy, setBusy] = useState(false)
  const joining = useRef(false)
  const [message, setMessage] = useState('Checking your private session…')
  useEffect(() => {
    let cancelled = false
    const checkSession = async () => {
      try {
        const response = await fetch('/session', { cache: 'no-store', credentials: 'same-origin' })
        if (!response.ok) throw new Error('The host is unavailable. Refresh to try again.')
        const session = await response.json()
        if (session.authMode === 'guest') {
          const profile = await getGuestProfile()
          if (cancelled) return
          setGuest(profile)
          setAuthMode('guest')
          setMessage(profile ? `Welcome back, ${profile.displayName}. Your character is ready.` : 'A fresh character is waiting for you. Pick a name, or let us choose one.')
        } else {
          if (cancelled) return
          setAuthMode('microsoft')
          setAuthorized(session.authorized === true)
          setMessage(session.authorized ? 'Your JERPG character and progress stay with your account.' : 'Open the private access link from your laptop to unlock sign-in.')
        }
      } catch (error) {
        if (!cancelled) setMessage(error instanceof Error ? error.message : 'The host is unavailable. Refresh to try again.')
      }
    }
    void checkSession()
    return () => { cancelled = true }
  }, [])

  const join = async () => {
    if (joining.current || !fsReady || !authMode || (authMode === 'microsoft' && !authorized)) return
    joining.current = true
    setBusy(true)
    try {
      if (authMode === 'guest') {
        const profile = await createGuestProfile(guest ? '' : displayName)
        setGuest(profile)
        setMessage(`Entering JERPG as ${profile.displayName}…`)
        dispatchEvent(new CustomEvent('connect', { detail: guestConnection(location.protocol, location.host, profile) }))
      } else {
        dispatchEvent(new CustomEvent('connect', { detail: privateConnection(location.protocol, location.host) }))
      }
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Unable to enter JERPG. Please try again.')
    } finally { joining.current = false; setBusy(false) }
  }

  if (modals || gameLoaded) return null
  const guestMode = authMode === 'guest'
  const canJoin = fsReady && !!authMode && (guestMode || authorized) && !busy
  return createPortal(<main className="cube-launcher">
    <div className="cube-brand"><span className="cube-mark">⬡</span><span>3CUBE</span><span className="cube-label">JERPG</span></div>
    <section className="cube-intro">
      <div className="cube-eyebrow">YOUR WORLD, CLOSER</div>
      <h1>Step inside<br/><em>JERPG.</em></h1>
      <p className="cube-lead">Explore the world from your browser.<br/>{guestMode ? 'Make yourself at home. Your adventure starts here.' : 'Enter VR to see it around you.'}</p>
      <form className="cube-entry" onSubmit={event => { event.preventDefault(); void join() }}>
        {guestMode && (guest ? <div className="cube-guest-profile">
          <span className="cube-guest-avatar" aria-hidden="true">{guest.displayName.slice(0, 1).toUpperCase()}</span>
          <div><span className="cube-guest-kicker">YOUR CHARACTER</span><strong>{guest.displayName}</strong><span>Saved in this browser</span></div>
        </div> : <div className="cube-name-field">
          <label htmlFor="cube-guest-name">Your name <span>optional</span></label>
          <input id="cube-guest-name" name="displayName" value={displayName} onChange={event => setDisplayName(event.target.value)} placeholder="Explorer" maxLength={16} pattern="[A-Za-z0-9_]{0,16}" autoComplete="nickname" autoCapitalize="none" spellCheck={false} aria-describedby="cube-name-help" disabled={busy}/>
          <p id="cube-name-help">Up to 16 letters, numbers, or underscores.</p>
        </div>)}
        <button className="cube-play" disabled={!canJoin} type="submit">
          {busy ? 'Getting your character ready…' : !fsReady ? 'Preparing client…' : !authMode ? 'Checking your session…' : guestMode ? 'Enter JERPG' : 'Sign in & join JERPG'}<span aria-hidden="true">↗</span>
        </button>
      </form>
      <p className="cube-session" role="status" aria-live="polite">{message}</p>
      {guestMode && <p className="cube-guest-note">Your guest identity is saved in this browser. Clearing browser data loses access to this character.</p>}
      <button className="cube-settings" onClick={() => openOptionsMenu('main')}>Settings</button>
    </section>
    <aside className="cube-guide">
      <div className="cube-eyebrow">TWO WAYS TO PLAY</div>
      <h2>Desktop or headset.</h2>
      <div className="cube-guide-row"><span>01</span><div><h3>{guestMode ? 'Make it your adventure' : authMode === 'microsoft' ? 'Sign in with Microsoft' : 'Get ready to explore'}</h3><p>{guestMode ? 'Choose a name, then enter. Your character stays with this browser.' : authMode === 'microsoft' ? 'Use the account you play Minecraft Java with.' : 'Checking how to enter this world…'}</p></div></div>
      <div className="cube-guide-row"><span>02</span><div><h3>Load into JERPG</h3><p>The server’s interface pack loads automatically.</p></div></div>
      <div className="cube-guide-row"><span>03</span><div><h3>Choose Enter VR</h3><p>On a supported headset, choose seated or standing and step into the world.</p></div></div>
      <div className="cube-controls"><strong>DESKTOP</strong><p>WASD move · Mouse look<br/>E inventory · Esc menu</p><strong>VR</strong><p>Left trigger teleport · Right stick turn<br/>Right grip interact · B / Y inventory</p></div>
    </aside>
    <footer>JERPG · Browser + VR <span>{guestMode ? 'Your browser. Your character.' : 'World & progress on JERPG'}</span></footer>
  </main>, document.body)
}
export default withInjectableUi(MainMenuRenderAppBase, 'mainMenuProvider')
